/**
 * Shared set-row parsing and display helpers for Program Insights (overview + per-lift cards).
 */

import { OUTCOME_STATUS } from "../../../../../constants";
import convertToRiR from "../../../../../utils/converters/convert-intensity";

const { COMPLETED } = OUTCOME_STATUS;

/** @readonly */
export const INSIGHTS_DATA_SOURCE = {
  PRESCRIBED: "prescribed",
  OUTCOMES: "outcomes",
};

/**
 * Parses a single positive finite number from a set field (reps, load, etc.).
 * @param {unknown} v
 * @returns {number}
 */
export function parsePositiveNumber(v) {
  if (v == null || v === "") {
    return 0;
  }
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Best-effort reps count for one set row (midpoint for ranges, including `repsLower` + `reps`).
 * @param {object|null|undefined} row
 * @returns {number}
 */
export function parseRepsForSet(row) {
  if (!row) {
    return 0;
  }
  const repsLower = parsePositiveNumber(row.repsLower);
  const repsUpper = parsePositiveNumber(row.reps);
  // Rep range: repsLower + reps (e.g. { repsLower: 8, reps: 12 }).
  if (repsLower > 0 && repsUpper > 0) {
    // e.g. { repsLower: 10, reps: 10 } → 10; { repsLower: 8, reps: 12 } → 10.
    return repsLower === repsUpper
      ? repsUpper
      : (repsLower + repsUpper) / 2;
  }
  // e.g. { reps: 5 } → 5; { reps: 12 } with no repsLower → 12.
  if (repsUpper > 0) {
    return repsUpper;
  }
  // e.g. { repsLower: 8 } with no reps → 8.
  if (repsLower > 0) {
    return repsLower;
  }
  // Legacy/unparsed range still in reps string (e.g. { reps: "8-12" }).
  if (typeof row.reps === "string" && row.reps.includes("-")) {
    const parts = row.reps.split("-");
    const rangeLower = parsePositiveNumber(parts[0]);
    const rangeUpper = parsePositiveNumber(parts[1] ?? parts[0]);
    // e.g. "8-12" → 10; "10-10" → 10.
    if (rangeLower > 0 && rangeUpper > 0) {
      return (rangeLower + rangeUpper) / 2;
    }
    // e.g. "8-" or "-12" — one side missing; use whichever bound parsed.
    return Math.max(rangeLower, rangeUpper);
  }
  return 0;
}

/** Function to parse the set multiplier for a set row.
 * @param {object|null|undefined} row
 * @returns {number}
 */
export function parseSetMultiplier(row) {
  const n = Number(row?.sets);
  if (Number.isFinite(n) && n > 0) {
    return Math.floor(n);
  }
  return 1;
}

/** Function to check if a set row is an AMRAP set row.
 * @param {object|null|undefined} row
 * @returns {boolean}
 */
export function isAmrapSetRow(row) {
  if (!row) {
    return false;
  }
  if (row.rpe === "AMRAP") {
    return true;
  }
  const repsStr = String(row.reps ?? "").trim().toLowerCase();
  if (repsStr.includes("amrap")) {
    return true;
  }
  return false;
}

/** Function to get the list of parameters for an exercise.
* @param {object|null|undefined} exercise */
function exerciseParametersList(exercise) {
  const params = exercise?.parameters;
  if (!params || params === "null") {
    return [];
  }
  return Array.isArray(params) ? params : [];
}

/** Function to check if a exercise uses a time parameter.
* @param {object|null|undefined} exercise */
export function exerciseUsesTimeParameter(exercise) {
  return exerciseParametersList(exercise).includes("Time");
}

/** Function to check if a exercise uses a reps parameter.
 *  @param {object|null|undefined} exercise */
export function exerciseUsesRepsParameter(exercise) {
  const list = exerciseParametersList(exercise);
  if (list.length === 0) {
    return true;
  }
  return list.includes("Reps");
}

/** Function to check if a set row has a time value.
 *  @param {object|null|undefined} row */
export function hasRowTimeValue(row) {
  if (row?.time == null || row.time === "") {
    return false;
  }
  if (parsePositiveNumber(row.time) > 0) {
    return true;
  }
  return String(row.time).trim().length > 0;
}

/**
 * Timed prescription: row has `time`, or exercise parameters are time-based (no Reps).
 * @param {object|null|undefined} row
 * @param {object|null|undefined} exercise
 */
export function isTimedSetRow(row, exercise) {
  if (!row) {
    return false;
  }
  if (hasRowTimeValue(row)) {
    return true;
  }
  return (
    exerciseUsesTimeParameter(exercise) && !exerciseUsesRepsParameter(exercise)
  );
}

/**
 * Reps counted toward Estimated Reps (overview / per-lift); AMRAP and timed → 0.
 * @param {object|null|undefined} row
 * @param {object|null|undefined} exercise
 */
export function estimatedRepsForSetRow(row, exercise) {
  if (isAmrapSetRow(row) || isTimedSetRow(row, exercise)) {
    return 0;
  }
  return parseRepsForSet(row);
}

/**
 * Reps counted toward volume & rep aggregates for a given data source.
 *
 * Prescribed (targets): AMRAP/timed rows have unknowable reps, so they are
 * excluded (0) — you can't compute a target volume for "as many as possible".
 * Outcomes (actuals): the reps were actually performed and logged as a number,
 * so count them directly. A completed AMRAP set keeps `rpe: "AMRAP"` on its
 * outcome, which would otherwise make `estimatedRepsForSetRow` drop its real
 * logged reps to 0 and undercount actual volume.
 *
 * @param {object|null|undefined} row
 * @param {object|null|undefined} exercise
 * @param {string} [dataSource]
 * @returns {number}
 */
export function countedRepsForSetRow(
  row,
  exercise,
  dataSource = INSIGHTS_DATA_SOURCE.PRESCRIBED
) {
  if (dataSource === INSIGHTS_DATA_SOURCE.OUTCOMES) {
    return parseRepsForSet(row);
  }
  return estimatedRepsForSetRow(row, exercise);
}

/** Function to get the volume display value for a set row.
 * @param {number} volumeKg
 * @param {(val: number, units?: string) => unknown} [fromMetric]
 * @param {"metric"|"imperial"} unitsKey
 * @returns {number}
 */
export function volumeDisplayValue(volumeKg, fromMetric, unitsKey) {
  if (!volumeKg || volumeKg <= 0) {
    return 0;
  }
  if (typeof fromMetric !== "function") {
    return Math.round(volumeKg);
  }
  const converted = fromMetric(volumeKg, unitsKey);
  if (converted == null) {
    return 0;
  }
  return Math.round(Number(converted));
}

/** Shown when a metric has no meaningful programmed data (not a literal zero). */
export const INSIGHTS_NO_DATA_DISPLAY = "-";

/*
* Function to format a count display value for insights.
* @param {number} n */
export function formatInsightsCountDisplay(n) {
  if (n == null || !Number.isFinite(n) || n <= 0) {
    return INSIGHTS_NO_DATA_DISPLAY;
  }
  return String(Math.round(n));
}

/*
* Function to format a percentage display value for insights.
* @param {number} pct */
export function formatInsightsPercentMetricDisplay(pct) {
  if (pct == null || !Number.isFinite(pct) || pct <= 0) {
    return INSIGHTS_NO_DATA_DISPLAY;
  }
  return `${Math.round(pct)}%`;
}

/** Volume on per-lift cards: no suffix when there is no load data. */
export function formatInsightsLiftVolumeDisplay(
  volumeKg,
  fromMetric,
  unitsKey,
  valueSuffix = ""
) {
  if (volumeKg == null || volumeKg <= 0) {
    return INSIGHTS_NO_DATA_DISPLAY;
  }
  return formatInsightsVolumeDisplayWithSuffix(
    volumeKg,
    fromMetric,
    unitsKey,
    valueSuffix
  );
}

/** Function to format the volume display value with a suffix.
 * @param {number} volumeKg
 * @param {(val: number, units?: string) => unknown} [fromMetric]
 * @param {"metric"|"imperial"} unitsKey
 * @param {string} [valueSuffix]
 * @returns {string}
 */
export function formatInsightsVolumeDisplayWithSuffix(
  volumeKg,
  fromMetric,
  unitsKey,
  valueSuffix = ""
) {
  const n = volumeDisplayValue(volumeKg, fromMetric, unitsKey);
  return `${Number(n).toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })}${valueSuffix ?? ""}`;
}

/** Function to compare a previous scalar to a current scalar.
 * @param {number|null|undefined} prevScalar
 * @param {number} curScalar
 * @returns {{ value: number, tone: "up"|"down"|"neutral" }|null}
 */
/**
 * Invokes `callback` for each prescribed set row with positive `sets` multiplier.
 *
 * @param {object|null|undefined} exercise
 * @param {(row: object, mult: number, exercise: object) => void} callback
 */
export function forEachPrescribedSetRow(exercise, callback) {
  const rows = Array.isArray(exercise?.sets) ? exercise.sets : [];
  for (const row of rows) {
    const mult = parseSetMultiplier(row);
    if (mult <= 0) {
      continue;
    }
    callback(row, mult, exercise);
  }
}

/** @param {object|null|undefined} exercise */
function repMaxKgForInsights(exercise) {
  const { primary, repMaximums, secondaryRepMaximum, type } = exercise ?? {};
  const repMax =
    type === "secondary" && secondaryRepMaximum
      ? secondaryRepMaximum
      : repMaximums?.[primary] || 0;
  return parsePositiveNumber(repMax);
}

/** @param {object|null|undefined} exercise @param {number} loadKg */
function percentRepMaxFromOutcomeLoad(exercise, loadKg) {
  const repMax = repMaxKgForInsights(exercise);
  if (repMax <= 0 || loadKg <= 0) {
    return 0;
  }
  return Math.round((loadKg / repMax) * 1000) / 10;
}

/**
 * @param {object|null|undefined} outcome
 * @param {object|null|undefined} setRow
 * @param {object|null|undefined} exercise
 */
function normalizeOutcomeToInsightsRow(outcome, setRow, exercise) {
  const loadKg = parsePositiveNumber(outcome?.load);
  return {
    sets: 1,
    load: loadKg || outcome?.load,
    reps: outcome?.reps,
    rpe: outcome?.rpe,
    percentRepMax:
      percentRepMaxFromOutcomeLoad(exercise, loadKg) ||
      parsePositiveNumber(setRow?.percentRepMax),
  };
}

/**
 * @param {object|null|undefined} exercise
 * @param {(row: object, mult: number, exercise: object) => void} callback
 */
function forEachOutcomeSetRowOnSingleExercise(exercise, callback) {
  const sets = Array.isArray(exercise?.sets) ? exercise.sets : [];
  for (const setRow of sets) {
    if (!setRow?.outcomes) {
      continue;
    }
    for (const entry of Object.values(setRow.outcomes)) {
      if (entry?.status !== COMPLETED || !entry?.outcome) {
        continue;
      }
      callback(
        normalizeOutcomeToInsightsRow(entry.outcome, setRow, exercise),
        1,
        exercise
      );
    }
  }
}

/**
 * Invokes `callback` for each completed outcome (actual logged set).
 *
 * @param {object|null|undefined} exercise
 * @param {(row: object, mult: number, exercise: object) => void} callback
 */
export function forEachOutcomeSetRow(exercise, callback) {
  if (Array.isArray(exercise?.exercises)) {
    for (const nestedExercise of exercise.exercises) {
      forEachOutcomeSetRowOnSingleExercise(nestedExercise, callback);
    }
    return;
  }
  forEachOutcomeSetRowOnSingleExercise(exercise, callback);
}

/**
 * Returns true if the exercise has at least one completed outcome set.
 * Used to decide whether a session counts as a training day in OUTCOMES mode.
 *
 * @param {object|null|undefined} exercise
 * @returns {boolean}
 */
export function exerciseHasCompletedOutcomes(exercise) {
  if (Array.isArray(exercise?.exercises)) {
    return exercise.exercises.some((e) => _hasCompletedOutcome(e));
  }
  return _hasCompletedOutcome(exercise);
}

function _hasCompletedOutcome(exercise) {
  const sets = Array.isArray(exercise?.sets) ? exercise.sets : [];
  return sets.some(
    (row) =>
      row?.outcomes &&
      Object.values(row.outcomes).some((e) => e?.status === COMPLETED && e?.outcome)
  );
}

/**
 * Prescribed program rows vs completed outcomes (Block Review actuals).
 *
 * @param {object|null|undefined} exercise
 * @param {(row: object, mult: number, exercise: object) => void} callback
 * @param {string} [dataSource]
 */
export function forEachInsightsSetRow(
  exercise,
  callback,
  dataSource = INSIGHTS_DATA_SOURCE.PRESCRIBED
) {
  if (dataSource === INSIGHTS_DATA_SOURCE.OUTCOMES) {
    forEachOutcomeSetRow(exercise, callback);
    return;
  }
  forEachPrescribedSetRow(exercise, callback);
}

/** Function to accumulate exercise set aggregates.
 * @param {object|null|undefined} ex
 * @returns {{ totalSets: number, estimatedReps: number, volumeKg: number }}
 */
export function accumulateExerciseSetAggregates(ex) {
  let totalSets = 0;
  let estimatedReps = 0;
  let volumeKg = 0;
  forEachPrescribedSetRow(ex, (row, mult) => {
    totalSets += mult;
    const reps = estimatedRepsForSetRow(row, ex);
    estimatedReps += mult * reps;
    const loadKg = parsePositiveNumber(row?.load);
    if (loadKg > 0 && reps > 0) {
      volumeKg += mult * reps * loadKg;
    }
  });
  return { totalSets, estimatedReps, volumeKg };
}

/** Function to round a delta value.
 *  @param {number} n */
export function roundInsightsDelta(n) {
  const rounded = Math.round(n * 10) / 10;
  return Object.is(rounded, -0) ? 0 : rounded;
}

/** @param {number} n */
export function formatInsightsMetricDisplay(n) {
  const r = roundInsightsDelta(n);
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/** @param {number} n */
export function formatInsightsDeltaDisplay(n) {
  return formatInsightsMetricDisplay(n);
}

export function deltaComparedToPrevious(prevScalar, curScalar) {
  if (prevScalar == null || curScalar == null) {
    return null;
  }
  const value = roundInsightsDelta(curScalar - prevScalar);
  if (value === 0) {
    return { value: 0, tone: "neutral" };
  }
  return { value, tone: value > 0 ? "up" : "down" };
}

/**
 * Display value for overview (RPE or RiR per coach preference).
 *
 * @param {number|null|undefined} avgRpe — volume-weighted mean on the RPE axis
 * @param {"rpe"|"rir"} intensityMeasure
 * @returns {number|null}
 */
export function avgPrescribedEffortDisplayValue(avgRpe, intensityMeasure = "rpe") {
  if (avgRpe == null || !Number.isFinite(avgRpe) || avgRpe <= 0) {
    return null;
  }
  const rounded = Math.round(avgRpe * 10) / 10;
  const display = convertToRiR(rounded, intensityMeasure === "rpe");
  const n = Number(display);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {number|null|undefined} avgRpe
 * @param {"rpe"|"rir"} [intensityMeasure]
 * @returns {string}
 */
export function formatAvgPrescribedEffortMetricDisplay(
  avgRpe,
  intensityMeasure = "rpe"
) {
  const display = avgPrescribedEffortDisplayValue(avgRpe, intensityMeasure);
  if (display == null) {
    return INSIGHTS_NO_DATA_DISPLAY;
  }
  return formatInsightsMetricDisplay(display);
}
